import { getCookie, setCookie } from "./utils/cookies"

import { SDKOptions } from "./sdk"

export type TState = {
    consumerToken?: string
    consumerTokenStatus?: "pending" | "success" | "error"
    customerWeakToken?: string
    customerStrongToken?: string
    customer_id?: string
    email?: string
    cart_id?: string
    cart_access_token?: string
    historic_cart_id?: string
    historic_cart_access_token?: string
}

const DAY = 60 * 60 * 24

// each cookie lives exactly as long as what it carries. a cookie outliving its content keeps a
// value around that cannot be used any more - and in the case of the strong token, a sensitive one
export const MAX_AGE = {
    // the api issues the weak token for three months of 31 days, the strong one for an hour
    customerWeakToken: 93 * DAY,
    customerStrongToken: 60 * 60,
    // belongs to the session the weak token opened
    customer_id: 93 * DAY,
    // carts are dropped by the api after two weeks
    cart: 14 * DAY,
}

// what is kept in a cookie whenever crossDomain is configured, so the other subdomains can see it
const COOKIE_KEYS = ["customerWeakToken", "customerStrongToken", "customer_id", "cart_id", "cart_access_token"] as const

// structural on purpose: everything that carries the two tokens and the customer id can open a
// session, without the state having to know the response types of the endpoints
export type Session = {
    customer_id: string
    token: {
        weak: { value: string }
        strong: { value: string }
    }
}

export class State {
    #options: SDKOptions
    #storage?: Storage
    // starts out as an empty state so that reading from it is safe before dehydrate has run
    #internalState: TState = {}
    #listeners = new Set<() => void>()

    constructor(options: SDKOptions, storage?: Storage) {
        this.#options = options
        this.#storage = storage

        this.#internalState = this.dehydrate()
    }

    subscribe = (listener: () => void) => {
        this.#listeners.add(listener)

        return () => {
            this.#listeners.delete(listener)
        }
    }

    // the snapshot is the in memory state itself: no storage read, no parsing and no allocation
    // per render. its identity only changes when something actually wrote to the state
    getSnapshot = () => {
        return this.#internalState
    }

    getServerSnapshot = () => {
        return this.#internalState
    }

    // picks up what another tab or another subdomain wrote. reading storage and cookies back in
    // is only needed for that, so it happens on demand instead of on every render
    sync = () => {
        const state = this.dehydrate()
        if (this.deepEqual(state, this.#internalState)) return

        this.#internalState = state
        this.notify()
    }

    // ACCESSORS =================================================================

    get consumerToken() {
        return this.#internalState.consumerToken
    }

    set consumerToken(token) {
        this.partialUpdate({ consumerToken: token })
    }

    get consumerTokenStatus() {
        return this.#internalState.consumerTokenStatus
    }

    set consumerTokenStatus(status) {
        this.partialUpdate({ consumerTokenStatus: status })
    }

    get customerWeakToken() {
        return this.getData("customerWeakToken")
    }

    set customerWeakToken(token) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-customerWeakToken`, token, {
                domain: this.#options.crossDomain,
                maxAge: MAX_AGE.customerWeakToken,
            })
        }
        this.partialUpdate({ customerWeakToken: token })
    }

    get customerStrongToken() {
        return this.getData("customerStrongToken")
    }

    set customerStrongToken(token) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-customerStrongToken`, token, {
                domain: this.#options.crossDomain,
                maxAge: MAX_AGE.customerStrongToken,
            })
        }
        this.partialUpdate({ customerStrongToken: token })
    }

    // CUSTOMER ==================================================================

    get customer_id() {
        return this.getData("customer_id")
    }

    set customer_id(id) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-customer_id`, id, {
                domain: this.#options.crossDomain,
                maxAge: MAX_AGE.customer_id,
            })
        }
        this.partialUpdate({ customer_id: id })
    }

    get email() {
        return this.#internalState.email
    }

    set email(email) {
        this.partialUpdate({ email: email })
    }

    // CART ======================================================================

    get cart_id() {
        return this.getData("cart_id")
    }

    set cart_id(id) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-cart_id`, id, {
                domain: this.#options.crossDomain,
                maxAge: MAX_AGE.cart,
            })
        }
        this.partialUpdate({ cart_id: id })
    }

    get cart_access_token() {
        return this.getData("cart_access_token")
    }

    set cart_access_token(token) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-cart_access_token`, token, {
                domain: this.#options.crossDomain,
                maxAge: MAX_AGE.cart,
            })
        }
        this.partialUpdate({ cart_access_token: token })
    }

    get historic_cart_id() {
        return this.#internalState.historic_cart_id
    }

    set historic_cart_id(id) {
        this.partialUpdate({ historic_cart_id: id })
    }

    get historic_cart_access_token() {
        return this.#internalState.historic_cart_access_token
    }

    set historic_cart_access_token(token) {
        this.partialUpdate({ historic_cart_access_token: token })
    }

    // SESSION ===================================================================

    setSession(session: Session) {
        this.customerWeakToken = session.token.weak.value
        this.customerStrongToken = session.token.strong.value
        this.customer_id = session.customer_id
    }

    // HELPER METHODS ============================================================

    partialUpdate(state: TState) {
        this.#internalState = Object.assign({}, this.#internalState, state)

        this.hydrate(this.#internalState)
        this.notify()
    }

    notify() {
        this.#listeners.forEach(listener => {
            listener()
        })
    }

    getData(key: keyof TState) {
        if (!this.#options.crossDomain || typeof document === "undefined") return this.#internalState[key]

        return getCookie(`${this.#options.storageKey}-${key}`)
    }

    deepEqual(a: any, b: any): boolean {
        return a && b && typeof a === "object" && typeof a === typeof b
            ? Object.keys(a).length === Object.keys(b).length &&
                  Object.keys(a).every(key => this.deepEqual(a[key], b[key]))
            : a === b
    }

    // STORAGE METHODS ===========================================================

    hydrate(state: TState) {
        if (!this.#storage) return

        const persistable = { ...state }

        // the status of a running consumer auth is not worth persisting
        delete persistable.consumerTokenStatus

        // whatever is kept in a cookie is not written a second time
        if (this.#options.crossDomain && typeof document !== "undefined") {
            COOKIE_KEYS.forEach(key => delete persistable[key])
        }

        this.#storage.setItem(this.#options.storageKey, JSON.stringify(persistable))
    }

    dehydrate(): TState {
        const state = this.readStorage()

        if (!this.#options.crossDomain || typeof document === "undefined") return state

        COOKIE_KEYS.forEach(key => {
            state[key] = this.getData(key)
        })

        return state
    }

    readStorage(): TState {
        if (!this.#storage) return {}

        const nativeValue = this.#storage.getItem(this.#options.storageKey)
        if (!nativeValue) return {}

        try {
            return JSON.parse(nativeValue)
        } catch {
            return {}
        }
    }
}
